<template>
  <fieldset id="biography-container" class="c-form__fields">
    <div class="c-container">
      <form ref="form" class="c-form" @submit.prevent="submit.exec">
        <div v-if="submit.err" class="c-message c-message--error u-mbm">
          <p>{{ t('updateProfileError') }}</p>
          <div v-if="biography.charLength > biography.charMaxLength">
            <p>({{ t('characterLimit') }})</p>
          </div>
        </div>
        <div class="c-form__field c-field">
          <div id="profile_biography" class="c-field__wrapper">
            <p ref="biography-text" class="c-presentation__text c-profile__biography-text">{{ biography.text.value }}</p>
            <div class="c-profile__biography-edit u-hide">
              <textarea
                ref="biography-textarea"
                v-model.trim="biography.text.value"
                cols="30"
                rows="10"
                :maxlength="biography.charMaxLength"
                :placeholder="t('biographyPlaceholder')"
                class="c-field__input c-profile__biography-textarea"
                @input="onTextareaInput"
              />
              <div class="character_counter">
                <small class="c-field__hint">0/0</small>
              </div>
            </div>
          </div>
        </div>
        <!-- submit -->
        <fieldset class="c-form__actions u-hide">
          <BtnSubmit id="button-submit-profile" ref="buttonSubmit" :loading="submit.loading" :label="t('updateProfile')" class="c-profile__button-submit" />
        </fieldset>
      </form>
    </div>
  </fieldset>
  <p id="biography-empty" class="biography-no-text u-hide">{{ t('biographyDefault') }}</p>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from '@/utils/i18n'
import { updateProfileBiography, updateDateModified, useRequest } from '@/utils/api'
import { formatDateTime } from '@/utils/format-date'

import BtnSubmit from '@/components/buttons/BtnSubmit.vue'

const { t } = useI18n({
  messages: {
    en: {
      name: 'Name',
      required: 'required',
      tags: 'Tags',
      updateProfile: 'Update',
      updateProfileError: 'Error while updating profile.',
      biographyDefault: "This user hasn't added a biography yet.",
      biographyPlaceholder: 'Write a paragraph about yourself (in english, 1200 characters maximum)',
      characterLimit: 'Maximum number of characters is limited to 1200.'
    },
    fr: {
      name: 'Nom',
      required: 'requis',
      tags: 'Tags',
      updateProfile: 'Mettre à jour',
      updateProfileError: 'Erreur lors de la mise à jour du profil.',
      biographyDefault: "Cet utilisateur n'a pas encore ajouté de biographie.",
      biographyPlaceholder: 'Ecrivez un paragraphe à propos de vous (en anglais, 1200 caractères maximum)',
      characterLimit: 'Le nombre de caractères maximum est limité à 1200.'
    }
  }
})

const props = defineProps<{
  userId: string,
  userName: string,
  profileId: number,
  profileName: string,
  profileBio: string,
}>()

let userId: number
let userName: string
let profileId: number
let profileName: string

let isSameUser: boolean

// Edit biography
interface Biography {
  reference: any,
  text: any,
  isEditable: boolean
  charLength: number
  charMaxLength: number
  editIcon: Element
  cancelIcon: Element
  confirmIcon: Element
  element: Element
  inputElement: Element
}

const biography: Biography = {
  reference: ref(props.profileBio),
  text: ref(props.profileBio),
  isEditable: false,
  charLength: 0,
  charMaxLength: 1200,
  editIcon: document.getElementsByClassName('biography-edit__icon')[0],
  cancelIcon: document.querySelector('.biography-cancel__icon')!,
  confirmIcon: document.getElementsByClassName('biography-confirm__icon')[0],
  element: document.getElementsByClassName('c-profile__biography-text')[0],
  inputElement: document.getElementsByClassName('c-profile__biography-edit')[0]
}

let isFirstEdit: boolean = true
let hasContent: boolean = props.profileBio.length > 0 && biography.text.value !== null && biography.text.value !== 'None'

let saveContent: string

let buttonSubmit: HTMLButtonElement

const form = ref<HTMLFormElement>()

let biographyContainerElement: HTMLElement
let biographyEmptyElement: HTMLElement

onMounted(() => {
  setProfile()

  setBiographyContent()

  if (isSameUser) {
    updateBiography()

    // update character counter
    updateCounter()
  }
})

onBeforeUnmount(() => {
  // remove event listeners
  if (biography.editIcon) {
    biography.editIcon.removeEventListener('click', () => {
      toggleEditMode()
    })

    biography.cancelIcon.removeEventListener('click', () => {
      biography.text.value = saveContent

      if (saveContent.length > 0 && biography.text.value !== 'None') {
        hasContent = true
      }

      if (biography.text.value === 'None') {
        biography.text.value = t('biographyPlaceholder')
      }

      toggleEditMode()
    })
  }

  const confirmButton = <HTMLElement>document.querySelector('.biography-confirm__icon')

  if (confirmButton) {
    confirmButton.removeEventListener('click', submitBiography)
  }
})

function setProfile () {
  userId = parseInt(props.userId)
  profileId = props.profileId

  userName = props.userName
  profileName = props.profileName

  isSameUser = userName === profileName

  // console.log('user id: ', userId)
  // console.log('user name: ', userName)
  // console.log('profile id: ', profileId)
  // console.log('profile name: ', profileName)
  // console.log('is same user: ', isSameUser)
}

function setBiographyContent (visible: boolean = true) {
  if (biography.text.value === 'None') {
    biography.text.value = ''
  }

  biographyContainerElement = document.querySelector('#biography-container')!
  biographyEmptyElement = document.querySelector('#biography-empty')!

  if (hasContent) {
    toggleElement(biographyContainerElement, visible)
    toggleElement(biographyEmptyElement, !visible)
  } else {
    toggleElement(biographyContainerElement, !visible)
    toggleElement(biographyEmptyElement, visible)
  }

  saveContent = props.profileBio
}

function updateBiography () {
  biography.element = document.getElementsByClassName('c-profile__biography-text')[0]
  biography.inputElement = document.getElementsByClassName('c-profile__biography-edit')[0]

  if (biography.editIcon) {
    // when clicking on biography edit icon
    biography.editIcon.addEventListener('click', () => {
      toggleEditMode()
    })

    biography.cancelIcon.addEventListener('click', () => {
      biography.text.value = saveContent

      if (saveContent.length > 0 && biography.text.value !== 'None') {
        hasContent = true
      }

      if (biography.text.value === 'None') {
        biography.text.value = ''
      }

      toggleEditMode()
    })
  }

  // submit
  buttonSubmit = <HTMLButtonElement>document.getElementById('button-submit-profile')

  const confirmButton = <HTMLElement>document.querySelector('.biography-confirm__icon')

  if (confirmButton) {
    confirmButton.addEventListener('click', submitBiography)
  }
}

function submitBiography () {
  if (buttonSubmit) {
    buttonSubmit.click()

    // reset biography field
    biography.isEditable = false

    toggleElement(biography.element, true)
    toggleElement(biography.inputElement, false)
    toggleElement(biography.editIcon, true)
    toggleElement(biography.cancelIcon, false)
    toggleElement(biography.confirmIcon, false)

    if (hasContent) {
      toggleElement(biographyContainerElement, true)
      toggleElement(biographyEmptyElement, false)
    } else {
      toggleElement(biographyContainerElement, false)
      toggleElement(biographyEmptyElement, true)
    }
  }
}

function toggleElement (el: Element, visible: boolean) {
  if (visible) {
    el.classList.add('u-show')
    el.classList.remove('u-hide')
  } else {
    el.classList.add('u-hide')
    el.classList.remove('u-show')
  }
}

function toggleEditMode () {
  biography.isEditable = !biography.isEditable

  if (biography.isEditable) {
    toggleElement(biography.editIcon, false)
    toggleElement(biography.cancelIcon, true)
    toggleElement(biography.confirmIcon, true)

    toggleElement(biography.element, false)
    toggleElement(biography.inputElement, true)

    if (!hasContent) {
      toggleElement(biographyContainerElement, true)
      toggleElement(biographyEmptyElement, false)
    } else {
      saveContent = biography.text.value
    }
  } else {
    toggleElement(biography.editIcon, true)
    toggleElement(biography.cancelIcon, false)
    toggleElement(biography.confirmIcon, false)

    toggleElement(biography.element, true)
    toggleElement(biography.inputElement, false)

    if (!hasContent) {
      toggleElement(biographyContainerElement, false)
      toggleElement(biographyEmptyElement, true)
    }
  }
}

function updateCounter () {
  if (props.profileBio === 'None') {
    biography.charLength = 0
  } else if (props.profileBio !== 'None') {
    biography.charLength = props.profileBio.length
  }

  if (props.profileBio.length > 0 && props.profileBio !== null) {
    const counterElement = document.getElementsByClassName('character_counter')[0]
    counterElement.innerHTML = `${biography.charLength}/${biography.charMaxLength}`
  }
}

function onTextareaInput (e: Event) {
  const target = e.target as HTMLInputElement

  if (target) {
    // After first edit
    if (isFirstEdit) {
      const biographyTextElement = document.getElementsByClassName('c-profile__biography-text')[0]
      biographyTextElement.classList.add('u-hide')
      biography.element = biographyTextElement

      isFirstEdit = false
    }

    biography.charLength = target.value.length
    if (biography.charLength === 0) {
      isFirstEdit = true

      hasContent = false

      const biographyTextElement = document.getElementsByClassName('c-profile__biography-text')[0]
      biographyTextElement.classList.add('u-hide')
    }

    if (biography.charLength > 0) {
      hasContent = true
    }

    const counterElement = document.getElementsByClassName('character_counter')[0]
    counterElement.innerHTML = `${biography.charLength}/${biography.charMaxLength}`
  }
}

const submit = useRequest(async () => {
  const body = {
    user: profileId,
    biography: biography.text.value
  }

  const data = await updateProfileBiography(userId, body)

  const currentDateTime = {
    user: profileId,
    date_modified: formatDateTime(new Date())
  }

  await updateDateModified(userId, currentDateTime)

  return data
},
{
  error () {
    // scroll to error message when an error occurs
    if (form.value) {
      form.value.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

</script>

<style scoped lang="scss">
.character_counter {
  float: right
}

</style>
